import React, { useMemo, useRef, useState } from 'react';
import {
  Box,
  Container,
  Flex,
  GridItem,
  Select,
  Text,
  chakra,
} from '@chakra-ui/react';
import { navigate } from 'gatsby';
import { chunk, sortBy } from 'lodash';
import { useDeepCompareEffect } from 'react-use';

import type {
  SanityDeptMarketingCallout,
  SanityGlobalProductCollection,
} from '../../../../../models/gatsby-graphql-types';
import { useUiComponentsInternationalContext } from '@repo/static-translations';
import { DeptGrid } from '@repo/ui-components/dept/design-system';
import MarketingCalloutCard from '../../../../tailwind/MarketingCalloutCard';
import CollectionFilters from '../../../../tailwind/CollectionGrid/CollectionFilters';
import ProductCard from '../../../../tailwind/ProductCard';
import Pagination from '../../../../tailwind/Pagination';
import { generateSectionId } from '@repo/ui-components/dept/utils';
import { getPageId } from '../../../../../utils/pagination';

import CurrentGroup from './CurrentGroup';
import FilterTags from './FilterTags';
import MobileFiltersAndSortMenu from './MobileFiltersAndSortMenu';
import getActiveFilters from './getActiveFilters';
import { SortersData } from './sorters';
import CurrentGroupMobile from './CurrentMobile';

interface Props {
  block: {
    collection: SanityGlobalProductCollection;
    marketingCallout: SanityDeptMarketingCallout;
  };
  data: {
    collection: SanityGlobalProductCollection;
  };
  location: {
    href: string;
    search: string;
    pathname: string;
  };
  pageContext: {
    collection: {
      currentGroup: object;
      allCategories: object[];
    };
    pathname: string;
    shopifyCollectionTitle: string;
  };
}

const CollectionGrid = ({
  block,
  data,
  location,
  pageContext,
}: Readonly<Props>) => {
  const ref = useRef(null);
  const { tt, localizePrice } = useUiComponentsInternationalContext();

  const [sort, setSort] = useState('');

  const pageId = getPageId(location);
  const searchParams = new URLSearchParams(location?.search);

  const onShopAllPage = pageContext?.pathname === 'shop';
  const bothPetTypesCollection = pageContext?.shopifyCollectionTitle
    ?.toLowerCase()
    .includes('all products');

  let products = useMemo(
    () => block?.collection?.products ?? data?.collection?.products ?? [],
    [block?.collection?.products, data?.collection?.products]
  );

  // filter out products not in the lifecycle array from collection
  const acceptedLifecycle = useMemo(
    () => ['Available', 'Coming Soon', 'Discontinued'],
    []
  );

  const SORTERS: SortersData = useMemo(
    () => ({
      featured: {
        title: tt(s => s.blocksDeptComponents.featuredCollectionSorterLabel),
        sortFunc: () => {},
      },
      newest: {
        title: tt(s => s.blocksDeptComponents.newestCollectionSorterLabel),
        reverse: true,
        sortFunc: product => product?.shopifyProduct?.publishedAt,
      },
      priceAsc: {
        title: tt(
          s => s.blocksDeptComponents.priceLowToHighCollectionSorterLabel
        ),
        sortFunc: product => {
          const prices = product.shopifyProduct.variants
            .filter(v => acceptedLifecycle.includes(v.variantLifecycle))
            .map(v => v.price);
          return Math.min(...prices);
        },
      },
      priceDesc: {
        title: tt(
          s => s.blocksDeptComponents.priceHighToLowCollectionSorterLabel
        ),
        reverse: true,
        sortFunc: product => {
          const prices = product.shopifyProduct.variants
            .filter(v => acceptedLifecycle.includes(v.variantLifecycle))
            .map(v => v.price);
          return Math.min(...prices);
        },
      },
    }),
    [acceptedLifecycle, tt]
  );

  products = products
    .filter(sanityProduct => !!sanityProduct?.shopifyProduct)
    .filter(sanityProduct =>
      acceptedLifecycle.includes(
        sanityProduct?.shopifyProduct?.productLifecycle
      )
    );

  const activeFilters = useMemo(
    () =>
      getActiveFilters({
        bothPetTypesCollection,
        location,
        onShopAllPage,
        products,
        priceFormatter: localizePrice,
        tt,
      }),
    [
      bothPetTypesCollection,
      localizePrice,
      location,
      onShopAllPage,
      products,
      tt,
    ]
  );

  const activeFilterTags = activeFilters
    .flatMap(({ filters }) => filters)
    .filter(f => f.active);

  const tagFilters = activeFilterTags
    .filter(({ type }) => type !== 'price')
    .map(({ tag }) => tag);

  if (tagFilters?.length) {
    products = products.filter(({ shopifyProduct }) =>
      tagFilters.every(tf => shopifyProduct.tags.includes(tf))
    );
  }

  const priceFilters = activeFilterTags.filter(({ type }) => type === 'price');

  if (priceFilters?.length) {
    products = products.filter(({ shopifyProduct }) => {
      const { amount } = shopifyProduct.priceRangeV2.minVariantPrice;
      return priceFilters.some(
        pf =>
          amount >= pf.range[0] && (pf.range[1] ? amount <= pf.range[1] : true)
      );
    });
  }

  const sortParam = searchParams.get('sort');
  if (Object.keys(SORTERS).includes(sortParam)) {
    products = sortBy(products, SORTERS[sortParam].sortFunc);
    if (SORTERS[sortParam].reverse) products.reverse();
  }

  const chunkSize = block.marketingCallout?.background ? 11 : 12;
  const chunks: any[][] = chunk(products, chunkSize);

  const handleSort = ({ target }) => {
    const sortSearchParams = new URLSearchParams(location?.search);
    sortSearchParams.set('sort', target.value);

    const newSearch = `?${sortSearchParams.toString()}`;
    if (newSearch !== location?.search) {
      navigate(newSearch);
    }
  };

  const handleFilters = ({ checked, filter, group }) => {
    const filterSearchParams = new URLSearchParams(location?.search);
    const urlSearchPriceParam = filterSearchParams.get(group);
    let newParams;

    if (checked) {
      newParams = urlSearchPriceParam
        ? `${urlSearchPriceParam},${filter.tag}`
        : filter.tag;
    } else {
      newParams = urlSearchPriceParam
        .split(',')
        .filter(f => f !== filter.tag)
        .join(',');
    }

    if (newParams) {
      searchParams.set(group, newParams);
    } else {
      searchParams.delete(group);
    }
    searchParams.set('page', '1');

    const newSearch = `?${searchParams.toString()}`;
    if (!location?.search?.length && newSearch === '?') return;

    if (newSearch !== location?.search) {
      navigate(newSearch);
    }
  };

  const handleFilterTag = (filter, group) => {
    handleFilters({
      checked: false,
      filter,
      group,
    });
  };

  const clearFilters = () => {
    const clearFilterSearchParams = new URLSearchParams('');
    if (sort) {
      clearFilterSearchParams.set('sort', sort);
    }
    clearFilterSearchParams.set('page', '1');

    const newSearch = `?${clearFilterSearchParams.toString()}`;
    if (newSearch !== location?.search) {
      navigate(newSearch);
    }
  };

  useDeepCompareEffect(() => {
    const scrollPosition = sessionStorage.getItem(
      `@@scroll|${location?.pathname}|${window?.history?.state?.key}`
    );
    if (ref?.current && !scrollPosition) {
      const filterScrollSearchParams = new URLSearchParams(location?.search);

      if (filterScrollSearchParams.size > 0) ref.current.scrollIntoView();
    }
  }, [location]);

  useDeepCompareEffect(() => {
    if (sortParam) setSort(sortParam);
    else setSort('');
  }, [sortParam]);

  const pageParam = searchParams?.get('page') ?? '1';
  const pageElements = chunks[parseInt(pageParam, 10) - 1 || 0] || [];
  if (
    block.marketingCallout &&
    !pageElements.some(el => el._type === 'deptMarketingCallout')
  ) {
    const { background } = block.marketingCallout;
    if (background?.asset?.url) {
      pageElements.splice(
        (block.marketingCallout?.position ?? 5) - 1,
        0,
        block.marketingCallout
      );
    }
  }

  if (!block || !data) return null;

  return (
    <Box
      {...generateSectionId(block)}
      bg={{
        base: 'linear-gradient(180deg, #F6F7F8 0%, #F6F7F8 100%) no-repeat 0 4rem',
        lg: '#F6F7F8',
      }}
    >
      <Container
        py={{ base: 4, lg: 10 }}
        ref={ref}
        sx={{ scrollMargin: '80px' }}
      >
        {pageContext?.collection?.currentGroup && (
          <Box w="full" display={{ lg: 'none' }}>
            <CurrentGroupMobile
              currentGroup={pageContext.collection.currentGroup}
            />
          </Box>
        )}

        <Box w="full" display={{ lg: 'none' }} pos="relative" mt="4">
          <Flex
            justify="space-between"
            align="center"
            py="2"
            pl="6"
            pr="2"
            pos="relative"
          >
            <Text
              textStyle="body"
              as="h4"
              pos="relative"
              fontWeight="light"
              mr="10"
              sx={{ textWrap: 'nowrap' }}
            >
              {
                chunks
                  .flatMap(c => [...c])
                  .filter(c => c._type !== 'deptMarketingCallout').length
              }{' '}
              {tt(s => s.blocksDeptComponents.resultsLabel)}
            </Text>
            <MobileFiltersAndSortMenu
              pageContext={pageContext}
              activeFilters={activeFilters}
              handleFilterTag={handleFilterTag}
              clearFilters={clearFilters}
              location={location}
              handleFilters={handleFilters}
              handleSort={handleSort}
              sort={sort}
              chunks={chunks}
            />
          </Flex>
        </Box>
        <DeptGrid zIndex={1}>
          <GridItem
            colSpan={3}
            zIndex={1}
            display={{ base: 'none', lg: 'block' }}
          >
            {pageContext?.collection?.currentGroup && (
              <CurrentGroup
                currentGroup={pageContext.collection.currentGroup}
              />
            )}

            {activeFilters.length ? (
              <FilterTags
                activeFilters={activeFilters}
                clearFilters={clearFilters}
                handleFilterTag={handleFilterTag}
                location={location}
              />
            ) : null}

            {activeFilters.length ? (
              <CollectionFilters
                activeFilters={activeFilters}
                handleFilters={handleFilters}
              />
            ) : null}
          </GridItem>
          <GridItem colSpan={{ base: 6, lg: 9 }}>
            <DeptGrid
              mb={6}
              templateColumns="repeat(6, 1fr)"
              columnGap={{ base: '0px', md: '1.5rem' }}
            >
              <GridItem colSpan={6}>
                <Flex
                  justify="space-between"
                  align="center"
                  py="2"
                  pl="6"
                  pr="2"
                  bg="primary.100"
                  pos="relative"
                  borderRadius="lg"
                  display={{ base: 'none', lg: 'flex' }}
                >
                  <Text textStyle="h4" as="h4" pos="relative" fontWeight="bold">
                    {
                      chunks
                        .flatMap(c => [...c])
                        .filter(c => c._type !== 'deptMarketingCallout').length
                    }{' '}
                    {tt(s => s.blocksDeptComponents.resultsLabel)}
                  </Text>
                  <Select
                    w="max"
                    onChange={handleSort}
                    value={sort}
                    bg="white"
                    textStyle="body"
                    fontWeight="light"
                  >
                    {Object.entries(SORTERS).map(([value, { title }]) => (
                      <chakra.option
                        key={value}
                        value={value}
                        textStyle="body"
                        fontWeight="light"
                      >
                        {title}
                      </chakra.option>
                    ))}
                  </Select>
                </Flex>
              </GridItem>
              {pageElements.map(p => (
                <GridItem
                  colSpan={{ base: 6, md: 3, xl: 2 }}
                  key={p._id ?? p?.title}
                >
                  {p._id ? (
                    <ProductCard product={p} />
                  ) : (
                    <MarketingCalloutCard
                      cta={p?.cta}
                      heading={p?.title}
                      image={p?.background}
                      promotionId={p?.promotionId}
                    />
                  )}
                </GridItem>
              ))}
            </DeptGrid>
            <Pagination numOfPages={chunks.length} page={pageId} />
          </GridItem>
        </DeptGrid>
      </Container>
    </Box>
  );
};

export default CollectionGrid;
